import { getQueryStringFromOptions, getResponse } from '@satellite/services/serviceUtilities';
import { cachedInflightRequest } from '@nova/core';
import { cacheableGet } from '@satellite/services/httpClient';

function toIsoDate(value) {
  return value?.toISO ? value.toISO() : value;
}

export default {
  async getOneById(observationId, params, options = {}) {
    const queryString = getQueryStringFromOptions(options);

    // Generate a unique cache key
    const cacheKey = `observations/getOneById:${observationId}:${JSON.stringify(
      params
    )}:${queryString}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`observations/${observationId}?${queryString}`, {
        params
      });
      return getResponse(response, options);
    });
  },
  async getManyByWarehouseId(warehouseId, params = {}, options = {}) {
    const queryString = getQueryStringFromOptions(options);

    // Generate a unique cache key
    const cacheKey = `observations/getMany:${JSON.stringify(params)}:${queryString}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`observations?${queryString}`, {
        params: { ...params, warehouseId }
      });

      return getResponse(response, options);
    });
  },
  async search(warehouseId, params = {}, options = {}) {
    // Generate a unique cache key
    const cacheKey = `observations-search/search:${JSON.stringify(params)}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.post(`observations-search/search`, { ...params, warehouseId });

      return getResponse(response, options);
    });
  },
  async searchFullText(warehouseId, params = {}, options = {}) {
    const { pagination = {}, sort, ...filters } = params;

    const cacheKey = `observations/searchFullText:${JSON.stringify(params)}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`observations`, {
        params: {
          ...filters,
          warehouseId,
          startDate: toIsoDate(filters.startDate),
          endDate: toIsoDate(filters.endDate),
          limit: pagination.limit,
          offset: pagination.offset
        }
      });

      return getResponse(response, options);
    });
  },
  async getGatesByWarehouseId(warehouseId) {
    const queryString = getQueryStringFromOptions();

    // Generate a unique cache key
    const cacheKey = `gates/getByWarehouseId:${warehouseId}:${queryString}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.get(`gates/${warehouseId}`);
      return getResponse(response);
    });
  },
  async addManualAssetVisit(observationId, payload) {
    // Generate a unique cache key
    const cacheKey = `observations/addManualAssetVisit:${observationId}`;

    return cachedInflightRequest(cacheKey, async () => {
      const response = await axios.post(`observations/${observationId}/asset-visit`, payload);
      return getResponse(response);
    });
  },
  /**
   * @template TResponse - The type of the response data
   *
   * Fetch autocomplete suggestions for SmartGate observations (license plates, DOT numbers).
   * @param {string} warehouseId - The warehouse identifier
   * @param {Object} [options] - Request options
   * @param {import('@satellite/services/serviceUtilities').GetResponseConfig} [options.getResponseConfig] - Response handling options (includeMetaData)
   * @param {import('axios').AxiosRequestConfig} [options.requestConfig] - Axios request config (params, signal, headers, etc.)
   * @param {boolean} [options.enableCache] - Enable inflight request caching (default: true; disabled automatically when signal is provided)
   * @returns {Promise<TResponse>}
   */
  async getAutocomplete(
    warehouseId,
    { getResponseConfig = {}, requestConfig = {}, enableCache = true } = {}
  ) {
    const url = `observations/public/${warehouseId}/autocomplete`;
    const axiosResponse = await cacheableGet(url, { ...requestConfig, enableCache });
    return getResponse(axiosResponse, getResponseConfig);
  }
};
